import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Toggle } from './ui/Toggle';
interface NavigationProps {
  mode: 'recruiter' | 'executive';
  onModeChange: (mode: 'recruiter' | 'executive') => void;
}
const navLinks = [{
  href: '#vision',
  label: 'Vision'
}, {
  href: '#leadership',
  label: 'Leadership'
}, {
  href: '#impact',
  label: 'Enterprise Impact'
}, {
  href: '#case-studies',
  label: 'Case Studies'
}, {
  href: '#thought-leadership',
  label: 'Thought Leadership'
}, {
  href: '#bio-kit',
  label: 'Bio Kit'
}, {
  href: '#contact',
  label: 'Contact'
}];
const modeOptions: [{
  id: string;
  label: string;
}, {
  id: string;
  label: string;
}] = [{
  id: 'recruiter',
  label: 'Recruiter View'
}, {
  id: 'executive',
  label: 'Executive View'
}];
export function Navigation({
  mode,
  onModeChange
}: NavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const isResumePage = location.pathname === '/resume';
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);
  const handleNavClick = (href: string) => {
    setIsMobileOpen(false);
    if (isResumePage) {
      // Navigate to home page with hash — let browser handle scroll
      window.location.href = '/' + href;
      return;
    }
    const el = document.querySelector(href);
    el?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <>
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-[#1C1F23]/97 backdrop-blur-md shadow-lg' : 'bg-[#1C1F23]'}`} role="banner" data-id="element-489">
        <nav className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation" data-id="element-490">
          <div className="flex items-center justify-between h-16 lg:h-18" data-id="element-491">
            {/* Logo / Name */}
            <Link to="/" className="flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57] rounded" aria-label="Michael K. Bigos — return to top" data-id="element-492">
              <span className="font-heading font-bold text-[#F7F7F5] text-lg tracking-tight" data-id="element-493">
                Michael K. <span className="text-[#B08D57]" data-id="element-494">Bigos</span>
              </span>
            </Link>

            {/* Desktop nav links */}
            <div className="hidden lg:flex items-center gap-1" data-id="element-495">
              {navLinks.map(link => <a key={link.href} href={link.href} onClick={e => {
              e.preventDefault();
              handleNavClick(link.href);
            }} className="px-3 py-2 text-sm font-body font-medium text-[#C4D4DC] hover:text-[#B08D57] transition-colors duration-200 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57] focus-visible:ring-offset-1 focus-visible:ring-offset-[#1C1F23]" data-id="element-496">
                  {link.label}
                </a>)}
              {/* Resume route link */}
              <Link to="/resume" className={`px-3 py-2 text-sm font-body font-medium transition-colors duration-200 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57] focus-visible:ring-offset-1 focus-visible:ring-offset-[#1C1F23] ${isResumePage ? 'text-[#B08D57]' : 'text-[#C4D4DC] hover:text-[#B08D57]'}`} data-id="element-497">
                Resume
              </Link>
            </div>

            {/* Mode toggle — desktop */}
            <div className="hidden lg:flex items-center gap-4" data-id="element-498">
              <Toggle options={modeOptions} activeId={mode} onChange={id => onModeChange(id as 'recruiter' | 'executive')} aria-label="Switch between Recruiter and Executive view" data-id="element-499" />
            </div>

            {/* Mobile hamburger */}
            <button className="lg:hidden p-2 rounded-lg text-[#F7F7F5] hover:text-[#B08D57] hover:bg-[#F7F7F5]/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57]" onClick={() => setIsMobileOpen(!isMobileOpen)} aria-expanded={isMobileOpen} aria-controls="mobile-menu" aria-label={isMobileOpen ? 'Close navigation menu' : 'Open navigation menu'} data-id="element-500">
              {isMobileOpen ? <XIcon className="w-6 h-6" aria-hidden="true" data-id="element-501" /> : <MenuIcon className="w-6 h-6" aria-hidden="true" data-id="element-502" />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile menu */}
      <AnimatePresence data-id="element-503">
        {isMobileOpen && <motion.div id="mobile-menu" className="fixed inset-0 z-30 bg-[#1C1F23] pt-16" initial={{
        opacity: 0,
        x: '100%'
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 40
      }} role="dialog" aria-label="Mobile navigation menu" data-id="element-504">
            <div className="flex flex-col h-full px-6 py-8 overflow-y-auto" data-id="element-505">
              <nav aria-label="Mobile navigation" data-id="element-506">
                <ul className="space-y-1" role="list" data-id="element-507">
                  {navLinks.map((link, i) => <motion.li key={link.href} initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.05 + 0.1
              }} data-id="element-508">
                      <a href={link.href} onClick={e => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }} className="block py-3 px-4 text-lg font-body font-medium text-[#D8E6EC] hover:text-[#B08D57] border-b border-[#F7F7F5]/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57] rounded" data-id="element-509">
                        {link.label}
                      </a>
                    </motion.li>)}
                  {/* Resume mobile link */}
                  <motion.li initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: navLinks.length * 0.05 + 0.1
              }} data-id="element-510">
                    <Link to="/resume" onClick={() => setIsMobileOpen(false)} className="block py-3 px-4 text-lg font-body font-medium text-[#B08D57] border-b border-[#F7F7F5]/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57] rounded" data-id="element-511">
                      Resume
                    </Link>
                  </motion.li>
                </ul>
              </nav>

              <div className="mt-8" data-id="element-512">
                <p className="text-xs font-body text-[#8FA4B0] uppercase tracking-widest mb-3" data-id="element-513">
                  View Mode
                </p>
                <Toggle options={modeOptions} activeId={mode} onChange={id => {
              onModeChange(id as 'recruiter' | 'executive');
              setIsMobileOpen(false);
            }} aria-label="Switch between Recruiter and Executive view" className="w-full" data-id="element-514" />
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}